import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { database } from './firebaseConfig';
import { unifiedFirebaseService } from '../services/firebaseService';

const fetchEvents = async (eventDatabase, startDate, endDate) => {
  try {
    if (unifiedFirebaseService.isElectron()) {
      console.log('[firebaseCRUD] Using unified service for fetchEvents');
      // Use the existing getEvents function from electronAPI with proper parameters
      const events = await window.electronAPI.getEvents(eventDatabase, startDate, endDate);
      
      if (!events || !events.success) {
        throw new Error(events?.error || 'Failed to fetch events');
      }
      
      // Convert timestamps to Date objects for consistency
      return events.events.map(event => ({
        ...event,
        start: event.start ? new Date(event.start) : null,
        end: event.end ? new Date(event.end) : null,
        createdAt: event.createdAt ? new Date(event.createdAt) : null,
        updatedAt: event.updatedAt ? new Date(event.updatedAt) : null,
      }));
    } else {
      console.log('[firebaseCRUD] Using web Firebase SDK for fetchEvents');
      const start = Timestamp.fromDate(startDate);
      const end = Timestamp.fromDate(endDate);
      
      const q = query(
        collection(database, eventDatabase),
        where('start', '>=', start),
        where('start', '<=', end)
      );
      
      const querySnapshot = await getDocs(q);
      const events = [];
      querySnapshot.forEach((doc) => {
        events.push({
          id: doc.id,
          ...doc.data(),
          start: doc.data().start.toDate(),
          end: doc.data().end.toDate()
        });
      });
      return events;
    }
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

const addEvents = async (eventDatabase, id, title, description, start, end, allDay, status, color, resource, editable, busy, createdAt, visibleInCalendar, subprojectStatus) => {
    try {
        if (unifiedFirebaseService.isElectron()) {
            console.log('[firebaseCRUD] Using unified service for addEvents');
            const eventData = {
              id: id,
              title: title,
              description: description,
              start: start,
              end: end,
              allDay: allDay,
              status: status,
              color: color,
              resource: resource,
              editable: editable,
              busy: busy,
              createdAt: createdAt,
              visibleInCalendar: visibleInCalendar || null,
              subprojectStatus: subprojectStatus || null,
            };
            const result = await unifiedFirebaseService.addDoc(eventDatabase, eventData);
            return result;
        } else {
            console.log('[firebaseCRUD] Using web Firebase SDK for addEvents');
            const docRef = await addDoc(collection(database, eventDatabase), {
              id: id,
              title: title,
              description: description,
              start: start,
              end: end,
              allDay: allDay,
              status: status,
              color: color,
              resource: resource,
              editable: editable,
              busy: busy,
              createdAt: createdAt,
              visibleInCalendar: visibleInCalendar || null,
              subprojectStatus: subprojectStatus || null,
            });
            return { id: docRef.id };
        }
    } catch (error) {
        console.error('Error adding event:', error);
        throw error;
    }
};

const updateEvent = async (eventDatabase, eventId, updatedData) => {
    try {
        if (unifiedFirebaseService.isElectron()) {
            console.log('[firebaseCRUD] Using unified service for updateEvent');
            // First find the document by id field (not eventId)
            const queryResult = await unifiedFirebaseService.queryDoc(eventDatabase, 'id', eventId);
            
            // Add proper data structure validation
            if (!queryResult || !queryResult.success) {
                throw new Error(queryResult?.error || 'Query failed');
            }
            
            // Check if data exists and has the expected structure
            if (!queryResult.data || !Array.isArray(queryResult.data)) {
                console.error('[firebaseCRUD] Unexpected query result structure:', queryResult);
                throw new Error('Invalid result structure');
            }
            
            if (queryResult.data.length === 0) {
                // Fallback: Some historical docs may not have a custom 'id' field; try using eventId as Firestore doc ID
                try {
                    console.warn('[firebaseCRUD] Query by custom id returned 0 docs. Falling back to docId update with id:', eventId);
                    return await unifiedFirebaseService.updateDoc(eventDatabase, eventId, updatedData);
                } catch (fallbackErr) {
                    console.error('[firebaseCRUD] Fallback update by docId failed:', fallbackErr);
                    throw new Error('Event not found');
                }
            }
            
            // The queryResult.data[0] contains { firestoreId: FirestoreDocID, id: CustomUUID, ...eventData }
            // We need the Firestore document ID for updateDoc
            const firestoreDocId = queryResult.data[0].firestoreId || queryResult.data[0]._firestoreId || queryResult.data[0]._id; // Attempt to read various docId aliases
            const customId = queryResult.data[0].id; // This is the custom UUID field
            
            if (!firestoreDocId) {
                console.error('[firebaseCRUD] Firestore document ID not found in result:', queryResult.data[0]);
                throw new Error('Firestore document ID not found');
            }
            
            console.log('[firebaseCRUD] Found document with Firestore ID:', firestoreDocId, 'and custom ID:', customId);
            return await unifiedFirebaseService.updateDoc(eventDatabase, firestoreDocId, updatedData);
        } else {
            console.log('[firebaseCRUD] Using web Firebase SDK for updateEvent');
            const q = query(collection(database, eventDatabase), where('id', '==', eventId));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                const docRef = doc(database, eventDatabase, querySnapshot.docs[0].id);
                await updateDoc(docRef, updatedData);
                return { success: true };
            } else {
                throw new Error('Event not found');
            }
        }
    } catch (error) {
        console.error('Error updating event:', error);
        throw error;
    }
};

const deleteEvent = async (eventDatabase, eventId) => {
    try {
        if (unifiedFirebaseService.isElectron()) {
            console.log('[firebaseCRUD] Using unified service for deleteEvent');
            // First find the document by id field (not eventId)
            const queryResult = await unifiedFirebaseService.queryDoc(eventDatabase, 'id', eventId);
            
            // Add proper data structure validation
            if (!queryResult || !queryResult.success) {
                throw new Error(queryResult?.error || 'Query failed');
            }
            
            // Check if data exists and has the expected structure
            if (!queryResult.data || !Array.isArray(queryResult.data)) {
                console.error('[firebaseCRUD] Unexpected query result structure:', queryResult);
                throw new Error('Invalid result structure');
            }
            
            if (queryResult.data.length === 0) {
                // Fallback: try using eventId as Firestore doc ID
                try {
                    console.warn('[firebaseCRUD] Query by custom id returned 0 docs. Falling back to docId delete with id:', eventId);
                    return await unifiedFirebaseService.deleteDoc(eventDatabase, eventId);
                } catch (fallbackErr) {
                    console.error('[firebaseCRUD] Fallback delete by docId failed:', fallbackErr);
                    throw new Error('Event not found');
                }
            }
            
            // The queryResult.data[0] contains { firestoreId: FirestoreDocID, id: CustomUUID, ...eventData }
            // We need the Firestore document ID for deleteDoc
            const firestoreDocId = queryResult.data[0].firestoreId || queryResult.data[0]._firestoreId || queryResult.data[0]._id; // Attempt to read various docId aliases
            const customId = queryResult.data[0].id; // This is the custom UUID field
            
            if (!firestoreDocId) {
                console.error('[firebaseCRUD] Firestore document ID not found in result:', queryResult.data[0]);
                throw new Error('Firestore document ID not found');
            }
            
            console.log('[firebaseCRUD] Found document with Firestore ID:', firestoreDocId, 'and custom ID:', customId);
            return await unifiedFirebaseService.deleteDoc(eventDatabase, firestoreDocId);
        } else {
            console.log('[firebaseCRUD] Using web Firebase SDK for deleteEvent');
            const q = query(collection(database, eventDatabase), where('id', '==', eventId));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                const docRef = doc(database, eventDatabase, querySnapshot.docs[0].id);
                return await deleteDoc(docRef);
            } else {
                throw new Error('Event not found');
            }
        }
    } catch (error) {
        console.error('Error deleting event:', error);
        throw error;
    }
};

// Remove undefined values from the top-level of the event before writing to Firestore
const sanitizeForFirestore = (obj) => {
    const entries = Object.entries(obj || {}).filter(([_, v]) => v !== undefined);
    return Object.fromEntries(entries);
};

const restoreEvent = async (eventDatabase, event) => {
    try {
        const sanitized = sanitizeForFirestore(event || {});
        if (unifiedFirebaseService.isElectron()) {
            console.log('[firebaseCRUD] Using unified service for restoreEvent');
            return await unifiedFirebaseService.addDoc(eventDatabase, sanitized);
        } else {
            console.log('[firebaseCRUD] Using web Firebase SDK for restoreEvent');
            const docRef = await addDoc(collection(database, eventDatabase), sanitized);
            return { id: docRef.id };
        }
    } catch (error) {
        console.error('Error restoring event:', error);
        throw error;
    }
};

// USER SETTINGS CRUD

const addUserSettings = async (settingsDatabase, id, title) => { // eslint-disable-line no-unused-vars
  try {
    await addDoc(collection(database, settingsDatabase), {
      id: id,
      title: title,
    });
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

const updateUserSetting = async (
  settingsDatabase,
  userEmail,
  settingToChange,
  newSettingValue
) => {
  // console.log(
  //   // "collectionId:",
  //   // collectionId,
  //   "userEmail:",
  //   userEmail,
  //   "settingToChange:",
  //   settingToChange,
  //   "newSettingValue:",
  //   newSettingValue,

  // );
  const userRef = collection(database, settingsDatabase);
  const q = query(userRef, where("userEmail", "==", `${userEmail}`));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    updateDoc(doc.ref, {
      ...doc.data(),
      [settingToChange]: newSettingValue,
    });
  });
};

const editUserSettingXXX = async (settingsDatabase, id, settingName) => { // eslint-disable-line no-unused-vars
  const userRef = collection(database, settingsDatabase);
  const q = query(userRef, where("id", "==", `${id}`));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    updateDoc(doc.ref, {
      settingToChange: settingName,
    });
  });
};

// TODO CRUD

const addTodoList = async (eventDatabase, id, title) => {
  try {
    await addDoc(collection(database, eventDatabase), {
      id: id,
      title: title,
    });
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

// const setListEdit = async (todoDatabase, id, editing) => {
//   const userRef = collection(database, todoDatabase);
//   const q = query(userRef, where('id', '==', `${id}`));
//   const querySnapshot = await getDocs(q);
//   querySnapshot.forEach((doc) => {
//     updateDoc(doc.ref, {
//       editing: editing
//     })
//   })
// }

const updateList = async (todoDatabase, id, title) => {
  const docRef = doc(database, todoDatabase, id);
  await updateDoc(docRef, {
    title: title,
  });
};

const updateCurrentList = async (todoDatabase, id, current) => {
  const docRef = doc(database, todoDatabase, id);
  await updateDoc(docRef, {
    current: current,
  });
};

const addTodo = async (
  eventDatabase,
  collectionId,
  id,
  title,
  desc,
  checked,
  editing,
  order
) => {
  try {
    await addDoc(collection(database, eventDatabase, collectionId, "todos"), {
      id: id,
      title: title,
      desc: desc,
      checked: checked,
      order: order,
    });
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

const updateTodo = async (
  eventDatabase,
  collectionId,
  id,
  title,
  description,
  checked,
  order
) => {
  // console.log(
  //   "collectionId:",
  //   collectionId,
  //   "Id:",
  //   id,
  //   "title:",
  //   title,
  //   "desc",
  //   description,
  //   "Checked",
  //   checked
  // );
  const userRef = collection(database, eventDatabase, collectionId, "todos");
  const q = query(userRef, where("id", "==", `${id}`));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    updateDoc(doc.ref, {
      id: id,
      title: title,
      desc: description,
      checked: checked,
      order: order,
    });
  });
};

const reorderTodo = async (eventDatabase, collectionId, id, order) => {
  // console.log(
  //   "collectionId:",
  //   collectionId,
  //   "order:",
  //   order,
  // );
  const userRef = collection(database, eventDatabase, collectionId, "todos");
  const q = query(userRef, where("id", "==", `${id}`));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    updateDoc(doc.ref, {
      ...doc.data(),
      order: order,
    });
  });
};

// const deleteList = async (eventDatabase, collectionId, todo) => {
//   const userRef = collection(database, eventDatabase, collectionId);
//   const q = query(userRef);
//   const querySnapshot = await getDocs(q);
//   querySnapshot.forEach((item) => {
//     try {
//       deleteDoc(doc(database, collectionId, item.ref.id))
//       console.log(`deleted todo with id: ${item.ref.id}`)
//     } catch (ex) {
//       console.error(`Delete FAILED: ${ex.message}`);
//       throw ex;
//     }
//   })
// };

const deleteList = async (eventDatabase, collectionId, listId) => {
  const userRef = collection(database, eventDatabase);
  const q = query(userRef, where("id", "==", listId));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((item) => {
    deleteAllTodos("todos", item.ref.id);
    try {
      deleteDoc(doc(database, eventDatabase, item.ref.id));
      console.log(`deleted todo with id: ${item.ref.id}`);
    } catch (ex) {
      console.error(`Delete FAILED: ${ex.message}`);
      throw ex;
    }
  });
};

const deleteAllTodos = async (eventDatabase, listId) => {
  const userRef = collection(database, eventDatabase, listId, "todos");
  const querySnapshot = await getDocs(userRef);
  querySnapshot.forEach((item) => {
    try {
      deleteDoc(doc(database, eventDatabase, listId, "todos", item.ref.id));
      console.log(`deleted todo with id: ${item.ref.id}`);
    } catch (ex) {
      console.error(`Delete FAILED: ${ex.message}`);
      throw ex;
    }
  });
};

const deleteTodo = async (eventDatabase, collectionId, todoId) => {
  const userRef = collection(database, eventDatabase, collectionId, "todos");
  const q = query(userRef, where("id", "==", todoId));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((item) => {
    try {
      deleteDoc(
        doc(database, eventDatabase, collectionId, "todos", item.ref.id)
      );
      console.log(`deleted todo with id: ${item.ref.id}`);
    } catch (ex) {
      console.error(`Delete FAILED: ${ex.message}`);
      throw ex;
    }
  });
};

export {
  fetchEvents,
  updateEvent,
  addEvents,
  deleteEvent,
  restoreEvent,
  updateUserSetting,
  addTodoList,
  addTodo,
  updateTodo,
  deleteTodo,
  deleteList,
  updateList,
  updateCurrentList,
  reorderTodo,
};
